import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { bind } from 'bind-event-listener';
import type { MessageDescriptor } from 'react-intl';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
import { v4 as uuid } from 'uuid';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';

import type { RemixHiddenOptionKind } from '../../types';
import {
	REMIX_MENU_COHORT_PARAM,
	REMIX_MENU_CONTROL_COHORT,
	REMIX_MENU_EXPERIMENT,
	REMIX_MENU_INLINE_DROPDOWN_COHORT,
} from './remix-config';
import {
	RemixOverlay as RemixOptionsOverlay,
	RemixPopup,
	type RemixCascadingMenuHeader,
	type RemixOption,
	type RemixOptionKind,
	type RemixOptionSelectionParameters,
	type RemixPopupPlacement,
} from './remix-overlay';
import { RemixThinkingBar } from './remix-thinking-bar';

const randomId = (): string => {
	if (globalThis?.crypto?.randomUUID) {
		return globalThis.crypto.randomUUID();
	}
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
	return uuid();
};

const styles = cssMap({
	anchorTrigger: {
		position: 'fixed',
		pointerEvents: 'none',
	},
});

/**
 * Builds a `RemixPopup` `trigger` render function from a plain DOM rect snapshot: an invisible,
 * fixed-position div matching the rect's bounds, which the popup anchors to. Used for entry points
 * that don't have a persistent trigger element (e.g. a floating/selection toolbar, or a button that
 * may unmount while the flow is active) — they instead capture `getBoundingClientRect()` (or the
 * current selection's rect) once, at click time.
 *
 * Portaled directly to the top-level document body: `RemixContainer` can be mounted anywhere in a product's DOM
 * tree (e.g. deep inside an editor's content component), and `position: fixed` is only viewport-
 * relative when none of its ancestors have a `transform`/`filter`/`will-change` — which editors
 * commonly do for scroll/sticky containers. Portaling avoids inheriting any such ancestor, so the
 * rect is always interpreted relative to the viewport, matching where it was captured.
 */
function buildAnchorRectTrigger(
	anchorRect: DOMRect,
): (triggerProps: TriggerProps) => React.ReactNode {
	return (triggerProps: TriggerProps) => {
		const document = getDocument();
		if (!document?.body) {
			return null;
		}

		return createPortal(
			<Box
				{...triggerProps}
				xcss={styles.anchorTrigger}
				style={{
					top: anchorRect.top,
					left: anchorRect.left,
					width: anchorRect.width,
					height: anchorRect.height,
				}}
				aria-hidden="true"
			/>,
			document.body,
		);
	};
}

/**
 * Props for the Remix container composed entry point.
 */
interface RemixContainerProps {
	/** Whether Remix is paused while the user answers a clarifying question. */
	isWaitingForInput?: boolean;
	/** Called when the user dismisses the remix flow (e.g. closes the overlay or preview). */
	onClose: () => void;
	remixAttemptId?: string;
	/** Optional callback when the user selects a remix type (e.g. chart, diagram, infographic, visualization, custom). */
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
	/**
	 * Called when "remix flow" becomes active or inactive.
	 * Handles the selection highlight (e.g. purple highlight) and thinking state start/stop flow
	 */
	onRemixFlowActiveChange?: (active: boolean) => void;
	/** When true, the thinking bar is owned by the caller and should not render here. */
	hideThinkingBar?: boolean;
	/**
	 * When true, the thinking bar is hidden
	 * Use this when the ephemeral view (e.g. staging modal with the result) is already visible
	 * so the thinking state is not shown at the same time as the result.
	 */
	isEphemeralViewVisible?: boolean;
	/**
	 * Set of remix option types that should skip the thinking bar entirely
	 * and proceed directly to the staging modal.
	 */
	skipThinkingTypes?: Set<string>;
	/** Custom set of options to display in the overlay. Defaults to selection-level Remix options. */
	options?: RemixOption[];
	/** Option kinds to hide from the chooser while keeping the public API strongly typed. */
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	/** Custom subtitle message for the overlay header. */
	subtitle?: MessageDescriptor;
	/** Whether to show the Labs badge in the Remix chooser. */
	showLabsLozenge?: boolean;
	/** Optional localized title and subtitle for the inline cascading menu. */
	cascadingMenuHeader?: RemixCascadingMenuHeader;
	/**
	 * Presentation mode for the remix chooser.
	 * - 'modal' (default): renders a centered ADS Modal
	 * - 'popup': renders an anchored ADS Popup next to a trigger element (e.g. object-level
	 *   remix, or a toolbar "Remix" button dropdown)
	 */
	presentation?: 'modal' | 'popup';
	/** Required when presentation is 'popup'. Render function for the trigger element. */
	trigger?: (triggerProps: TriggerProps) => React.ReactNode;
	/**
	 * Alternative, lighter-weight anchor for popup presentation: a DOM rect snapshot (e.g. from
	 * `event.currentTarget.getBoundingClientRect()`) captured at click time, for entry points that
	 * don't have a persistent trigger element to anchor to (e.g. a floating/selection toolbar, or a
	 * button that may unmount while the flow is active). When the
	 * `cc-ce-maui-remix-menu-multivariant` experiment cohort is `variant1` and an `anchorRect`
	 * is supplied (and no explicit `trigger` was given), `RemixContainer` renders an anchored popup instead of
	 * `presentation`'s modal/popup choice, using a synthetic invisible trigger positioned at the
	 * rect. This is the single place that decides modal vs. popup for every Remix entry point.
	 */
	anchorRect?: DOMRect;
	/** Popup placement relative to the trigger. Only used when presentation is 'popup'. */
	popupPlacement?: RemixPopupPlacement;
	popupOffset?: [number, number];
	/** Whether the popup is open. Only used when presentation is 'popup'. */
	isPopupOpen?: boolean;
	/**
	 * When true, picking an option does nothing: no thinking state and no `onOptionSelect`. The
	 * chooser stays open and `onClose` keeps working. Use while another Remix flow is active.
	 */
	isOptionSelectBlocked?: boolean;
	/** Close an open cascading popup when an external scroll detaches it from its view-mode trigger. */
	shouldCloseOnScroll?: boolean;
	/** Keep DOM focus where it is while pressing the popup menu. Opt in for entry points that are unmounted when the editor blurs. */
	shouldPreserveEditorFocus?: boolean;
	/** When this value changes the popup will reposition itself. */
	popupRepositionKey?: number;
	/** Replace the animated rainbow border with a loading progress bar. */
	showProgressBar?: boolean;
	/**
	 * When `showProgressBar`, rapidly fill to 100%. Flip this on once the underlying
	 * work completes, then gate the next UI state on `onCompleteAnimationFinished`.
	 */
	isComplete?: boolean;
	/**
	 * Optional controlled thinking state. When provided, this takes precedence over
	 * the container's local thinking state. should be used with `onStopThinking`
	 */
	isThinking?: boolean;
	/** Fires once the progress bar finishes animating to 100%. */
	onCompleteAnimationFinished?: () => void;
	/**
	 * The content type of the document being edited (e.g. `'jira.issue'`, `'confluence.page'`).
	 */
	contentType?: string;
	/**
	 * When set, the overlay option-picker is suppressed on the first render and thinking
	 * begins immediately. Used when a prompt is pre-supplied (e.g. from the AI Image
	 * Generation flow) so the user never sees the option-picker.
	 */
	autoSubmitUserPrompt?: string;
	/**
	 * When set, the option-picker is fully suppressed and the flow starts immediately
	 * for this selection. Used when the picker lives elsewhere (e.g. the editor toolbar
	 * "Remix" dropdown IS the picker), so the in-app chooser must never render.
	 */
	autoSubmitSelection?: RemixOptionSelectionParameters;
	/** Optional callback when the user stops the thinking process. should be used with `isThinking` */
	onStopThinking?: (options?: { reopenModal?: boolean }) => void;
	/**
	 * When false, shows image-generation-specific thinking messages instead of remix messages.
	 * Defaults to true (remix entrance text).
	 */
	isRemixEntrance?: boolean;
	/** Center the fixed thinking bar over the content container. */
	shouldMeasureContentBounds?: boolean;
}

/**
 * Composed entry point for the Remix feature.
 *
 * Renders the RemixOptionsOverlay for the user to pick a remix type.
 * Thinking bar visibility is internal (flow active and not ephemeral visible).
 */
export function RemixContainer({
	isWaitingForInput = false,
	onClose,
	remixAttemptId,
	onOptionSelect,
	onRemixFlowActiveChange,
	hideThinkingBar = false,
	isEphemeralViewVisible = false,
	skipThinkingTypes,
	options,
	hiddenOptions,
	subtitle,
	showLabsLozenge = true,
	cascadingMenuHeader,
	presentation = 'modal',
	trigger,
	anchorRect,
	popupPlacement,
	popupOffset,
	isPopupOpen = true,
	isOptionSelectBlocked = false,
	shouldCloseOnScroll = false,
	shouldPreserveEditorFocus = false,
	popupRepositionKey,
	showProgressBar = false,
	isComplete = false,
	isThinking: controlledIsThinking,
	onCompleteAnimationFinished,
	contentType,
	autoSubmitUserPrompt,
	autoSubmitSelection,
	onStopThinking,
	isRemixEntrance = true,
	shouldMeasureContentBounds = false,
}: RemixContainerProps): React.ReactElement {
	// for editor, isThinking will be passed in, which is stored in editor state
	// Auto-submitted prompts/selections should skip the option picker on first render.
	const [localIsThinking, setLocalIsThinking] = useState(
		!!autoSubmitUserPrompt || !!autoSubmitSelection,
	);
	const isThinking = controlledIsThinking ?? localIsThinking;
	const [localRemixAttemptId, setLocalRemixAttemptId] = useState<string>();
	const localRemixAttemptIdRef = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (!isThinking || remixAttemptId) {
			localRemixAttemptIdRef.current = undefined;
			setLocalRemixAttemptId(undefined);
			return;
		}

		localRemixAttemptIdRef.current ??= randomId();
		setLocalRemixAttemptId(localRemixAttemptIdRef.current);
	}, [isThinking, remixAttemptId]);
	const currentRemixAttemptId = remixAttemptId ?? localRemixAttemptId;
	const [selectedOptionType, setSelectedOptionType] = useState<RemixOptionKind | undefined>();
	// Prevent the option picker flashing after a cancelled auto-submit flow.
	const [isCancelledAutoSubmit, setIsCancelledAutoSubmit] = useState(false);
	const showThinkingBar =
		isThinking &&
		currentRemixAttemptId !== undefined &&
		!isEphemeralViewVisible &&
		!hideThinkingBar;

	// Read the cohort without recording an exposure. Only the control and variant1 groups ever
	// open this component, so recording the exposure here would leave the variant2 group
	// uncounted. The exposure is recorded by each Remix entry point instead, at the moment the
	// cohort decides which Remix picker to open, which is a moment all three groups share.
	const remixMenuCohort = UNSAFE_expValNoExposure(
		REMIX_MENU_EXPERIMENT,
		REMIX_MENU_COHORT_PARAM,
		REMIX_MENU_CONTROL_COHORT,
	);
	const isDropdownVariantEnabled = remixMenuCohort === REMIX_MENU_INLINE_DROPDOWN_COHORT;
	const effectiveTrigger =
		trigger ??
		(isDropdownVariantEnabled && anchorRect ? buildAnchorRectTrigger(anchorRect) : undefined);
	const effectivePresentation: 'modal' | 'popup' =
		isDropdownVariantEnabled && effectiveTrigger ? 'popup' : presentation;

	// When a selection is pre-supplied (the picker lives elsewhere, e.g. the editor
	// toolbar Remix dropdown), fire it once on mount and never render the in-app
	// picker. This runs even when a controlled `isThinking={false}` is passed.
	const hasAutoSubmittedSelectionRef = React.useRef(false);
	useEffect(() => {
		if (hasAutoSubmittedSelectionRef.current || !autoSubmitSelection) {
			return;
		}
		hasAutoSubmittedSelectionRef.current = true;
		setSelectedOptionType(autoSubmitSelection.type);
		setLocalIsThinking(true);
		onRemixFlowActiveChange?.(true);
		onOptionSelect?.(autoSubmitSelection);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoSubmitSelection?.type, autoSubmitSelection?.subType, autoSubmitSelection?.userPrompt]);

	const handleOptionSelect = useCallback(
		(params: RemixOptionSelectionParameters) => {
			if (isOptionSelectBlocked) {
				return;
			}
			if (!skipThinkingTypes?.has(params.type)) {
				setLocalIsThinking(true);
			}
			setSelectedOptionType(params.type);
			onRemixFlowActiveChange?.(true);
			onOptionSelect?.(params);
		},
		[isOptionSelectBlocked, onOptionSelect, onRemixFlowActiveChange, skipThinkingTypes],
	);

	const handleStopThinking = useCallback(() => {
		// Record the local cancellation before aborting the request or dismissing the host UI. The
		// lifecycle owner uses this intent to distinguish a Loading Bar stop from a Rovo-side abort;
		// calling onStopThinking first can release the request and unmount the owner before that
		// distinction reaches the machine settlement handler.
		onRemixFlowActiveChange?.(false);
		setLocalIsThinking(false);
		if (autoSubmitUserPrompt) {
			setIsCancelledAutoSubmit(true);
		}
		if (fg('platform_editor_ai_remix_toolbar')) {
			onStopThinking?.();
		}
		// for insertion, when cancel, the logic is from ProseMirror, we'll show the modal again and
		// - we call onRemixFlowActiveChange in useEffect to cancel the request
		// but for update, when cancel, the logic is from UI, we have to call onRemixFlowActiveChange manually
		// - cause the component tree will be unmounted and the useEffect will not be called
	}, [autoSubmitUserPrompt, onRemixFlowActiveChange, onStopThinking]);

	// only when show the thinking bar, we handle the escape key to stop the thinking
	// when the thinking bar is rendered in editor, the escape will also be handled by editor
	useEffect(() => {
		if (fg('platform_editor_ai_remix_toolbar')) {
			if (!showThinkingBar) {
				return;
			}
		} else {
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				return;
			}

			if (!isThinking) {
				return;
			}
		}

		return bind(window, {
			type: 'keydown',
			listener: (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					handleStopThinking();
				}
			},
		});
	}, [isThinking, handleStopThinking, showThinkingBar]);

	// Keep the trigger mounted so Popup can stay anchored.
	if (effectivePresentation === 'popup' && effectiveTrigger) {
		return (
			<>
				<RemixPopup
					isOpen={isPopupOpen && !isThinking}
					onClose={onClose}
					trigger={effectiveTrigger}
					placement={popupPlacement}
					offset={popupOffset}
					useCascadingMenu={isDropdownVariantEnabled}
					cascadingMenuHeader={cascadingMenuHeader}
					shouldCloseOnScroll={shouldCloseOnScroll}
					shouldPreserveEditorFocus={shouldPreserveEditorFocus}
					onOptionSelect={handleOptionSelect}
					options={options}
					hiddenOptions={hiddenOptions}
					subtitle={subtitle}
					showLabsLozenge={showLabsLozenge}
					repositionKey={popupRepositionKey}
				/>
				{showThinkingBar && (
					<RemixThinkingBar
						isWaitingForInput={isWaitingForInput}
						remixAttemptId={currentRemixAttemptId}
						loadingBarType="big-bar"
						onStop={handleStopThinking}
						optionType={selectedOptionType}
						showProgressBar={showProgressBar}
						isComplete={isComplete}
						onCompleteAnimationFinished={onCompleteAnimationFinished}
						contentType={contentType}
						isRemixEntrance={isRemixEntrance}
						shouldMeasureContentBounds={shouldMeasureContentBounds}
					/>
				)}
			</>
		);
	}

	// Modal mode (default).
	if (isThinking) {
		if (showThinkingBar) {
			return (
				<RemixThinkingBar
					isWaitingForInput={isWaitingForInput}
					remixAttemptId={currentRemixAttemptId}
					loadingBarType="big-bar"
					onStop={handleStopThinking}
					optionType={selectedOptionType}
					showProgressBar={showProgressBar}
					isComplete={isComplete}
					onCompleteAnimationFinished={onCompleteAnimationFinished}
					contentType={contentType}
					isRemixEntrance={isRemixEntrance}
					shouldMeasureContentBounds={shouldMeasureContentBounds}
				/>
			);
		}
		return <></>;
	}

	// Auto-submit cancellation leaves reopening to the caller.
	if (isCancelledAutoSubmit) {
		return <></>;
	}

	// Never render the in-app option-picker when a selection is pre-supplied — the
	// picker lives elsewhere (e.g. the editor toolbar Remix dropdown). The mount
	// only exists to run the flow; the auto-submit effect above starts it.
	if (autoSubmitSelection) {
		return <></>;
	}

	return (
		<RemixOptionsOverlay
			onClose={onClose}
			onOptionSelect={handleOptionSelect}
			options={options}
			hiddenOptions={hiddenOptions}
			subtitle={subtitle}
			showLabsLozenge={showLabsLozenge}
		/>
	);
}
